/**
 * v130 — Per-session shot aggregates.
 *
 * Computes mean radius, group center, 95% group radius, best/worst, and
 * per-distance breakdowns from a list of RichShots.
 *
 * Math:
 *   - group center = (mean(hitX), mean(hitY))
 *   - radial offset per shot = sqrt((hitX - cx)² + (hitY - cy)²)
 *   - meanRadius = mean of radial offsets from POA (0,0)
 *   - group95Radius = 2σ of radial offsets from GROUP CENTER (≈95% within)
 *
 * Why two different radii: meanRadius answers "how far off from where you
 * aimed" (accuracy). group95Radius answers "how tight is your group around
 * its own center" (precision). Both matter for a tune; they diverge when the
 * sight is off.
 */

import type { RichShot, ShotAggregates } from './v2-schema';
import type { SetAggregates, OverlayLegendRow } from './v3-schema';
import { OVERLAY_PALETTE, colorForSource } from './v3-schema';

export function computeShotAggregates(shots: readonly RichShot[]): ShotAggregates | null {
  if (shots.length === 0) return null;

  // Mean radial offset from POA (accuracy)
  const radiiFromPOA = shots.map((s) => Math.sqrt(s.hitXIn ** 2 + s.hitYIn ** 2));
  const meanRadiusIn = radiiFromPOA.reduce((a, b) => a + b, 0) / radiiFromPOA.length;
  const bestRadiusIn = Math.min(...radiiFromPOA);
  const worstRadiusIn = Math.max(...radiiFromPOA);

  // Group center
  const centerX = shots.reduce((a, s) => a + s.hitXIn, 0) / shots.length;
  const centerY = shots.reduce((a, s) => a + s.hitYIn, 0) / shots.length;

  // 95% group radius around group center: 2σ of radial offsets from center
  const radiiFromCenter = shots.map((s) =>
    Math.sqrt((s.hitXIn - centerX) ** 2 + (s.hitYIn - centerY) ** 2),
  );
  const meanRadialFromCenter = radiiFromCenter.reduce((a, b) => a + b, 0) / radiiFromCenter.length;
  const variance =
    radiiFromCenter.reduce((a, r) => a + (r - meanRadialFromCenter) ** 2, 0) /
    Math.max(1, radiiFromCenter.length - 1);
  const stddev = Math.sqrt(variance);
  // 2σ → ~95% within; for tighter groups (variance < 0.01) we use the max
  // radius from center as a more honest "actual extent" since 2σ on a near-
  // perfect group gives 0 which is misleading.
  const group95RadiusIn =
    radiiFromCenter.length >= 3
      ? meanRadialFromCenter + 2 * stddev
      : Math.max(...radiiFromCenter);

  // Per-distance breakdown
  const perDistance: Record<number, { count: number; meanRadiusIn: number }> = {};
  for (const s of shots) {
    const d = s.distanceYd;
    const entry = perDistance[d] ?? { count: 0, meanRadiusIn: 0 };
    const r = Math.sqrt(s.hitXIn ** 2 + s.hitYIn ** 2);
    // Running mean update
    entry.meanRadiusIn = (entry.meanRadiusIn * entry.count + r) / (entry.count + 1);
    entry.count += 1;
    perDistance[d] = entry;
  }

  return {
    count: shots.length,
    meanRadiusIn,
    group95RadiusIn,
    centerX,
    centerY,
    bestRadiusIn,
    worstRadiusIn,
    perDistance,
  };
}

/**
 * v132 — Compute per-set aggregates (drops the per-distance breakdown — sets
 * are typically at one distance, and the legend doesn't need it).
 *
 * When a set has only one shot, group95RadiusIn is reported as 0 — there's no
 * spread to measure. Caller should display "n/a" or hide the column.
 */
export function computeSetAggregates(shots: readonly RichShot[]): SetAggregates | null {
  const base = computeShotAggregates(shots);
  if (!base) return null;
  return {
    count: base.count,
    meanRadiusIn: base.meanRadiusIn,
    group95RadiusIn: base.group95RadiusIn,
    centerX: base.centerX,
    centerY: base.centerY,
    bestRadiusIn: base.bestRadiusIn,
    worstRadiusIn: base.worstRadiusIn,
  };
}

/**
 * v132 — Compute Compare Overlay legend rows.
 *
 * `sources` is a list of `{ sourceId, label, shots }`. The function assigns a
 * stable color from `OVERLAY_PALETTE` (cycled by index, then hashed for any
 * source beyond the palette length so two sources never collide repeatedly).
 *
 * Returns the legend rows in input order so the caller controls sort.
 */
export function computeOverlayStats(
  sources: ReadonlyArray<{ sourceId: string; label: string; shots: readonly RichShot[] }>,
): OverlayLegendRow[] {
  return sources.map((src, idx) => {
    const color = idx < OVERLAY_PALETTE.length
      ? OVERLAY_PALETTE[idx]
      : colorForSource(src.sourceId);
    return {
      sourceId: src.sourceId,
      label: src.label,
      color,
      shotCount: src.shots.length,
      aggregates: computeSetAggregates(src.shots),
    };
  });
}

